/**
 * Consumo da plataforma — Consumo IA e Volumetria.
 *
 * Separado do DashboardContextService pelo mesmo motivo do lado da API: aquele
 * serve o dashboard principal (acompanhamento legislativo), este serve a aba de
 * Consumo, que é domínio novo e evolui junto dos agentes de IA.
 *
 * As URLs permanecem sob /dashboard/* — o contrato HTTP não mudou nesta separação.
 */

import * as http from '../helpers/http';
import { format } from 'date-fns';

export interface IVolumetriaNovasProposicoesMes {
    mes: string;
    senado: number;
    camara: number;
    congresso: number;
    estaduais_municipais: number;
}

export interface IVolumetriaNovasProposicoesResponse {
    series: IVolumetriaNovasProposicoesMes[];
    totais: {
        senado: number;
        camara: number;
        congresso: number;
        estaduais_municipais: number;
    };
}

// Consumo IA do mês corrente, SÓ em legisdots — a API não envia custo, modelo nem tokens.
export type TAgenteConsumoIA = 'jabuti' | 'mercados' | 'sumarizacao' | 'perfil' | 'smart_talk' | 'nota_tecnica';

export interface IConsumoIAAgente {
    agente: TAgenteConsumoIA;
    processamentos: number;
    legisdots: number;
    media_por_unidade: number;
    ativo: boolean;            // flag de processamento do agente
}

export interface IConsumoIAResponse {
    mes: string;               // YYYY-MM
    configurado: boolean;
    franquia: number;          // legisdots mensais do contrato
    contratados: number;       // franquia + acréscimos aprovados no mês
    consumidos: number;
    faltam: number;
    percentual: number;
    limite_atingido: boolean;
    projecao: {
        dias_mes: number;
        dia_atual: number;
        consumo_projetado: number;
        indicacao_contratar: number;
    };
    agentes: IConsumoIAAgente[];
}

export interface ISolicitacaoLegisdotsResponse {
    id: number;
    quantidade: number;
    status: string;
    solicitado_em: string;
}

interface IResultado<T> { ok: boolean; data?: T; mensagem?: string }

class ConsumoService {
    GetVolumetriaNovasProposicoes = async (dtIni: Date, dtFin: Date): Promise<IVolumetriaNovasProposicoesResponse> => {
        const result = await http.get(
            '/dashboard/volumetria/novas-proposicoes/' +
            format(dtIni, 'yyyy-MM-dd') + '/' + format(dtFin, 'yyyy-MM-dd')
        );
        if (result.success) { return result.data }
        return undefined;
    };

    GetConsumoIA = async (): Promise<IConsumoIAResponse> => {
        const result = await http.get('/dashboard/consumo-ia');
        if (result.success) { return result.data }
        return undefined;
    };

    // Popup "Contratação de Legisdots": registra a SOLICITAÇÃO; a plataforma aprova.
    SolicitarLegisdots = async (quantidade: number): Promise<IResultado<ISolicitacaoLegisdotsResponse>> => {
        const result = await http.post('/dashboard/consumo-ia/solicitacao', { quantidade, aceite_termo: true });
        if (result.success) return { ok: true, data: result.data };
        return { ok: false, mensagem: result.data?.message };
    };

    // Switch do agente: grava a flag de processamento (HAB_*) e devolve as habilitações atualizadas.
    AlterarAgenteIA = async (codigo: string, ativo: boolean): Promise<IResultado<any>> => {
        const result = await http.put(`/parametros-cliente/${codigo}`, { valor: ativo ? 'true' : 'false' });
        if (result.success) return { ok: true, data: result.data };
        return { ok: false, mensagem: result.data?.message };
    };
}

export default new ConsumoService();
